/**
 * AI Chatbot floating button — powered by Claude Haiku via /api/chatbot/ask.
 * Maintains a session-scoped conversation. Friendly, helps users discover tools
 * and answer pricing / how-to questions.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, X, Send, Bot, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';
const SUGGESTIONS = [
  "How do I find best keywords?",
  "Show me free SEO tools",
  "What's the price of Pro plan?",
  "How does AI Keyword Generator work?",
];

function makeSessionId() {
  const KEY = "mm-chatbot-session-v1";
  const existing = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
  if (existing) return existing;
  const fresh = "chat-" + Math.random().toString(36).slice(2, 10);
  try { localStorage.setItem(KEY, fresh); } catch (_) {}
  return fresh;
}

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(makeSessionId);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! 👋 I'm MarkeeMix AI — ask me about our 30+ marketing tools, pricing, or how to grow your business." },
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages((m) => [...m, userMsg]);
    setDraft("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/chatbot/ask`, {
        session_id: sessionId,
        message: text.trim(),
      });
      const reply = r.data?.reply || "Sorry, I couldn't generate a reply.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Hmm, I hit a snag. Try again in a moment, or hit WhatsApp for live support." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ai-chatbot-launcher"
        style={{
          position: "fixed", bottom: 24, right: 24,
          width: 56, height: 56, borderRadius: "50%",
          background: "linear-gradient(135deg, #7C3AED, #EC4899)",
          color: "#fff", border: "none", cursor: "pointer", zIndex: 90,
          boxShadow: "0 10px 28px rgba(124,58,237,0.45), 0 0 0 6px rgba(124,58,237,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "transform 0.18s ease",
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.08)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
        aria-label="Open AI assistant"
      >
        <Bot size={26}/>
        <span style={{
          position: "absolute", top: -4, right: -4,
          width: 14, height: 14, background: "#10B981", borderRadius: "50%",
          border: "2px solid #fff",
        }}/>
      </button>

      {open && (
        <div
          data-testid="ai-chatbot-panel"
          style={{
            position: "fixed", bottom: 92, right: 24, width: 360, maxWidth: "calc(100vw - 32px)",
            height: 520, maxHeight: "calc(100vh - 140px)",
            background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 16,
            boxShadow: "0 20px 50px rgba(15,23,42,0.25)", zIndex: 91,
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          <header style={{
            padding: "14px 16px", background: "linear-gradient(135deg, #7C3AED, #EC4899)",
            color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Sparkles size={18}/>
              <div>
                <strong style={{ fontSize: 14 }}>MarkeeMix AI</strong>
                <div style={{ fontSize: 11, opacity: 0.9 }}>Powered by Claude Haiku · Online</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-chatbot-close"
              aria-label="Close chat"
              style={{ background: "rgba(255,255,255,0.18)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14}/></button>
          </header>

          <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: 14, background: "#F8FAFC" }}>
            {messages.map((m, i) => (
              <div key={i} data-testid={`chatbot-msg-${i}`} style={{
                display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 8,
              }}>
                <div style={{
                  maxWidth: "82%",
                  padding: "10px 12px", borderRadius: 12,
                  background: m.role === "user" ? "linear-gradient(135deg, #7C3AED, #EC4899)" : "#FFFFFF",
                  color: m.role === "user" ? "#fff" : "#0F172A",
                  border: m.role === "user" ? "none" : "1px solid #E2E8F0",
                  fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap",
                }}>{m.content}</div>
              </div>
            ))}
            {busy && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", color: "#64748B", fontSize: 12 }}>
                <Loader2 size={12} className="animate-spin"/> Thinking…
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div style={{ padding: "6px 10px", display: "flex", flexWrap: "wrap", gap: 6, background: "#F8FAFC" }}>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  data-testid={`chatbot-suggestion-${i}`}
                  style={{ fontSize: 11, padding: "5px 9px", background: "#EDE9FE", color: "#5B21B6", border: "1px solid #C4B5FD", borderRadius: 999, cursor: "pointer", fontWeight: 600 }}
                >{s}</button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(draft); }}
            style={{ padding: 10, borderTop: "1px solid #E2E8F0", display: "flex", gap: 6, background: "#FFFFFF" }}
          >
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask anything…"
              data-testid="chatbot-input"
              style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E2E8F0", background: "#F8FAFC", color: "#0F172A", fontSize: 13, outline: "none" }}
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              data-testid="chatbot-send"
              style={{
                padding: "0 14px", background: "linear-gradient(135deg, #7C3AED, #EC4899)", color: "#fff", border: "none", borderRadius: 10, cursor: busy ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600,
                opacity: busy || !draft.trim() ? 0.55 : 1,
              }}
            ><Send size={13}/></button>
          </form>
        </div>
      )}
    </>
  );
}
